import type { Content } from '../../models';

import { VisualsWithDataTypes } from '../../config/constants';
import { getValueFieldName, getRightLabelFieldName } from '../../config/dimension.config';
import { cloneObject } from '../../utilities';
import { getQuillFromFields } from '../base-objects';
import { getExtendedFormatObject } from '../style';

const smoothCurve = (x: number): number => {
  // Extended key points for both negative and positive values
  const keyPoints = [
    { x: -100, y: -1 }, // Asymptotic towards -1
    { x: -5, y: -0.9 },
    { x: -1, y: -0.5 },
    { x: 0, y: 0 },
    { x: 1, y: 1 }, // Asymptotic towards 1
  ];

  // If x is out of bounds, clamp it
  if (x <= keyPoints[0].x) return keyPoints[0].y;
  if (x >= keyPoints[keyPoints.length - 1].x) return keyPoints[keyPoints.length - 1].y;

  // Find the two key points that surround x
  for (let i = 0; i < keyPoints.length - 1; i++) {
    const p1 = keyPoints[i];
    const p2 = keyPoints[i + 1];

    if (x >= p1.x && x <= p2.x) {
      // Smoothstep interpolation
      const t = (x - p1.x) / (p2.x - p1.x);
      const smoothT = t * t * (3 - 2 * t); // Smoothstep for a smooth transition
      return p1.y + smoothT * (p2.y - p1.y);
    }
  }

  return 0; // Fallback, should never reach here
};

export const validateContentVersion68 = (content: Content): boolean => {
  const { type, typeSpecific } = content;

  if (typeSpecific.version < 68) {
    if (VisualsWithDataTypes.includes(type) && typeSpecific.chart) {
      // Build quills
      if (typeSpecific.dimensions && typeSpecific.fields) {
        const defaultQuillStyle = getExtendedFormatObject();
        const valueFieldName = getValueFieldName(typeSpecific.dimensions);
        const labelFieldName = getRightLabelFieldName(type, typeSpecific.dimensions);

        let labelQuill = JSON.stringify({ ops: [{ insert: '\n' }] });
        let valueQuill = JSON.stringify({ ops: [{ insert: '\n' }] });

        if (valueFieldName) {
          const valueField = typeSpecific.fields[valueFieldName];
          valueQuill = getQuillFromFields([valueField]);
        }

        if (labelFieldName) {
          const labelField = typeSpecific.fields[labelFieldName];
          labelQuill = getQuillFromFields([labelField]);
        }

        const firstLabelStyle = getExtendedFormatObject();
        firstLabelStyle.useDataColor = true;

        // Dot Objects
        typeSpecific.chart.dots = typeSpecific.chart.dots ?? {
          symbol: 'circle',
          size: 15,
          labelPosition: 'top',
          customDots: [],
          ranges: [],
          paddingInner: 0.1,
          paddingOuter: 0.1,
          extraLabels: {
            enabled: false,
            mode: 'diff',
            style: cloneObject(defaultQuillStyle),
            quill: valueQuill,
            background: { backgroundColor: '#fff' },
            position: 'top',
          },
          firstLabel: {
            enabled: false,
            style: cloneObject(firstLabelStyle),
            quill: labelQuill,
            background: { backgroundColor: '#fff' },
            line: {
              enabled: true,
              stroke: '#ccc',
              strokeWidth: 1,
              strokeDasharray: 'solid',
            },
          },
        };
      }

      // Dynamic label for horizontal types
      typeSpecific.chart.dynamicLabelHeight = true;

      const { bars } = typeSpecific.chart;
      if (bars.paddingInner < -1 || bars.paddingInner > 1) {
        bars.paddingInner = smoothCurve(bars.paddingInner);
      }

      if (bars.paddingOuter < -1 || bars.paddingOuter > 1) {
        bars.paddingOuter = smoothCurve(bars.paddingOuter);
      }

      if (bars.groupPaddingInner < -1 || bars.groupPaddingInner > 1) {
        bars.groupPaddingInner = smoothCurve(bars.groupPaddingInner);
      }

      if (bars.groupPaddingOuter < -1 || bars.groupPaddingOuter > 1) {
        bars.groupPaddingOuter = smoothCurve(bars.groupPaddingOuter);
      }
    }

    typeSpecific.version = 68;
  }

  return true;
};
